
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Settings, Users, AlertTriangle } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface AffiliateDefaultLimitsSettingsProps {
  affiliateDefaults: { daily: number; monthly: number } | null;
  availableToAllocate: { daily: number; monthly: number };
  userCount: number;
  isUpdatingDefaults: boolean;
  isApplyingDefaults: boolean;
  onSetDefaults: (daily: number, monthly: number) => void;
  onApplyToAll: (daily: number, monthly: number) => void;
}

export const AffiliateDefaultLimitsSettings: React.FC<AffiliateDefaultLimitsSettingsProps> = ({
  affiliateDefaults,
  availableToAllocate,
  userCount,
  isUpdatingDefaults,
  isApplyingDefaults,
  onSetDefaults,
  onApplyToAll
}) => {
  const isMobile = useIsMobile();
  const [newDefaults, setNewDefaults] = useState({
    daily: affiliateDefaults?.daily || 3,
    monthly: affiliateDefaults?.monthly || 20
  });

  const handleSetDefaults = () => {
    onSetDefaults(newDefaults.daily, newDefaults.monthly);
  };

  const handleApplyToAll = () => {
    onApplyToAll(newDefaults.daily, newDefaults.monthly);
  };

  const willLimitExceedQuota = () => {
    const totalRequired = {
      daily: newDefaults.daily * userCount,
      monthly: newDefaults.monthly * userCount
    };
    
    return totalRequired.daily > availableToAllocate.daily || 
           totalRequired.monthly > availableToAllocate.monthly;
  };

  return (
    <Card>
      <CardHeader className={isMobile ? 'p-3' : ''}>
        <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : 'text-lg'}`}>
          <Settings className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          Default Limits Settings
        </CardTitle>
      </CardHeader>
      <CardContent className={`space-y-${isMobile ? '3' : '4'} ${isMobile ? 'p-3 pt-0' : ''}`}>
        {/* Current Defaults Display */}
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'} ${isMobile ? 'p-3' : 'p-4'} bg-muted/50 rounded-lg`}>
          <div>
            <p className={`font-medium ${isMobile ? 'text-sm' : ''}`}>Current Default Limits</p>
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
              Applied to new users automatically
            </p>
          </div>
          <div className={`flex gap-2 ${isMobile ? 'flex-wrap' : ''}`}>
            <Badge variant="outline" className={isMobile ? 'text-xs' : ''}>
              Daily: {affiliateDefaults?.daily || 3}
            </Badge>
            <Badge variant="outline" className={isMobile ? 'text-xs' : ''}>
              Monthly: {affiliateDefaults?.monthly || 20}
            </Badge>
          </div>
        </div>

        {/* Set New Defaults */}
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
          <div>
            <Label htmlFor="defaultDaily" className={isMobile ? 'text-xs' : ''}>Default Daily Limit</Label>
            <Input
              id="defaultDaily"
              type="number"
              min="0"
              value={newDefaults.daily}
              onChange={(e) => setNewDefaults({
                ...newDefaults,
                daily: parseInt(e.target.value) || 0
              })}
              className={isMobile ? 'h-8 text-sm mt-1' : ''}
            />
          </div>
          <div>
            <Label htmlFor="defaultMonthly" className={isMobile ? 'text-xs' : ''}>Default Monthly Limit</Label>
            <Input
              id="defaultMonthly"
              type="number"
              min="0"
              value={newDefaults.monthly}
              onChange={(e) => setNewDefaults({
                ...newDefaults,
                monthly: parseInt(e.target.value) || 0
              })}
              className={isMobile ? 'h-8 text-sm mt-1' : ''}
            />
          </div>
        </div>

        {willLimitExceedQuota() && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className={isMobile ? 'text-xs' : ''}>
              Warning: Applying these defaults to all {userCount} users would require 
              {newDefaults.daily * userCount} daily and {newDefaults.monthly * userCount} monthly quota,
              but you only have {availableToAllocate.daily} daily and {availableToAllocate.monthly} monthly available.
            </AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'gap-2'}`}>
          <Button
            onClick={handleSetDefaults}
            disabled={isUpdatingDefaults}
            size={isMobile ? "sm" : "sm"}
            className={isMobile ? 'h-8 text-xs' : ''}
          >
            {isUpdatingDefaults ? 'Setting...' : 'Set as Default'}
          </Button>

          <Dialog>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size={isMobile ? "sm" : "sm"}
                disabled={willLimitExceedQuota()}
                className={isMobile ? 'h-8 text-xs' : ''}
              >
                <Users className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                Apply to All Users
              </Button>
            </DialogTrigger>
            <DialogContent className={isMobile ? 'w-[95vw] max-w-md' : ''}>
              <DialogHeader>
                <DialogTitle className={isMobile ? 'text-base' : ''}>Apply Default Limits to All Users</DialogTitle>
                <DialogDescription className={isMobile ? 'text-xs' : ''}>
                  This will update all {userCount} referred users to use the new default limits 
                  ({newDefaults.daily} daily, {newDefaults.monthly} monthly).
                  Custom user limits will be preserved.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className={isMobile ? 'text-xs' : ''}>
                    This action will consume significant quota and cannot be undone.
                    Make sure you have enough available allocation.
                  </AlertDescription>
                </Alert>
                <Button
                  onClick={handleApplyToAll}
                  disabled={isApplyingDefaults || willLimitExceedQuota()}
                  className={`w-full ${isMobile ? 'h-8 text-xs' : ''}`}
                >
                  {isApplyingDefaults ? 'Applying...' : 'Confirm & Apply to All'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className={`${isMobile ? 'text-xs' : 'text-xs'} text-muted-foreground space-y-1`}>
          <p>• Set as Default: Only changes the default for new users</p>
          <p>• Apply to All: Updates all existing users with default limits</p>
        </div>
      </CardContent>
    </Card>
  );
};
